import { useMemo } from 'react';
import { NoShowRateByType } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { AnalyticsHooks, ChartCTA, DemoChip, InfoTipPopover } from '@frontend/analytics-shared';
import { CategoryBarChartData, Chart } from '@frontend/charts';
import { i18next, useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { NSRTypeComparisonText } from '../components/nsr-type-comparison-text';
import { useFetchNSROILocationsNoShowRate } from '../hooks/use-fetch-nsr-locations-no-show-rate';
import { useNSRRoiShallowStore } from '../hooks/use-nsr-roi-store';
import { commonMultiAppearance } from './common-values';
import { CommonChartProps } from './types';

const labels = {
  existingPatients: i18next.t('Existing Patients', { ns: 'analytics' }),
  groupNameHeader: i18next.t('Location', { ns: 'analytics' }),
  newPatients: i18next.t('New Patients', { ns: 'analytics' }),
};

const colors = {
  existingPatients: theme.colors.primary20,
  newPatients: theme.colors.warning20,
};

const strokeColors = {
  existingPatients: theme.colors.primary40,
  newPatients: theme.colors.warning40,
};

// The keys used here are based on how BE sends the data.
const dataKeys: Record<string, string> = {
  'New Appt': 'newPatients',
  'Follow Up Appt': 'existingPatients',
};

export const NSRRoiLocationsComparisonPatientType = ({ isDemoAccount }: CommonChartProps) => {
  const { t } = useTranslation('analytics');
  const { filters, setFilters } = useNSRRoiShallowStore('filters', 'setFilters');
  const { locationNames } = AnalyticsHooks.useAnalyticsOrgLocations({
    module: 'ROI',
  });

  const { data, isLoading } = useFetchNSROILocationsNoShowRate({
    noShowRateType: NoShowRateByType.PATIENT_TYPE,
    filters,
  });

  const chartData: CategoryBarChartData = useMemo(() => {
    return (
      data?.data?.locations?.reduce<CategoryBarChartData>((acc, { aggregates = [], locationId = '' }) => {
        // Create a temporary map for quick lookup of values from aggregates
        const aggregateValueMap = aggregates.reduce<Record<string, number>>((mapAcc, { name = '', value = 0 }) => {
          const chartKey = dataKeys[name];

          if (chartKey) {
            mapAcc[chartKey] = value;
          }

          return mapAcc;
        }, {});

        const orderedChartDataKeys = Object.values(dataKeys);

        // Build the location's data object ensuring the order of orderedChartDataKeys
        const locationData: Record<string, number> = {};
        orderedChartDataKeys.forEach((chartKey) => {
          locationData[chartKey] = aggregateValueMap[chartKey] || 0;
        });

        acc[locationId] = locationData;
        return acc;
      }, {}) || {}
    );
  }, [data?.data?.locations]);

  const { highlight, description } = useMemo(() => {
    const locationCount = Object.keys(chartData).length;

    if (!locationCount) {
      return {
        highlight: '',
        description: '',
      };
    }

    const { sumOfNewPatients, sumOfExistingPatients } = Object.values(chartData).reduce(
      (acc, { newPatients, existingPatients }) => {
        return {
          sumOfNewPatients: (acc.sumOfNewPatients || 0) + Math.round(newPatients || 0),
          sumOfExistingPatients: (acc.sumOfExistingPatients || 0) + Math.round(existingPatients || 0),
        };
      },
      {
        sumOfNewPatients: 0,
        sumOfExistingPatients: 0,
      }
    );

    const avgNewPatientsNoShow = sumOfNewPatients / locationCount;
    const avgExistingPatientsNoShow = sumOfExistingPatients / locationCount;

    if (avgNewPatientsNoShow > avgExistingPatientsNoShow) {
      return {
        highlight: labels.newPatients,
        description: t('Send additional reminders for new patients to reduce no-shows'),
      };
    } else if (avgExistingPatientsNoShow > avgNewPatientsNoShow) {
      return {
        highlight: labels.existingPatients,
        description: t('Send additional reminders for existing patients to reduce no-shows'),
      };
    } else {
      return {
        highlight: t('Equal No-Show Rates'),
        description: t('Send additional reminders for new and existing patients to reduce no-shows'),
      };
    }
  }, [chartData, t]);

  return (
    <Chart
      colors={colors}
      isLoading={isLoading}
      labels={{ ...labels, ...locationNames }}
      strokeColors={strokeColors}
      style={{ marginBottom: 0 }}
    >
      <Chart.Header
        title={t('No-Show Rate by Patient Type')}
        infoTip={
          <InfoTipPopover>
            {t(
              'A detailed breakdown of no-show rates between new patients and existing patients, allowing practices to identify which group is more likely to miss appointments and tailor reminder or follow-up strategies to reduce no-shows'
            )}
          </InfoTipPopover>
        }
        leftElement={isDemoAccount && <DemoChip />}
      />
      <Chart.CategoryBarChart
        appearance={{
          ...commonMultiAppearance(filters),
          customCategoryNameRenderer: (category) => {
            switch (category) {
              case 'newPatients':
                return t('New Patients No-Show Rate');

              case 'existingPatients':
                return t('Existing Patients No-Show Rate');

              default:
                return undefined;
            }
          },
          customTooltipData: (groupId) => [
            {
              color: colors.newPatients,
              formattedValue: formatters.appendPercent(chartData[groupId]?.newPatients),
              id: 'newPatients',
              label: labels.newPatients,
              value: chartData[groupId]?.newPatients,
            },
            {
              color: colors.existingPatients,
              formattedValue: formatters.appendPercent(chartData[groupId]?.existingPatients),
              id: 'existingPatients',
              label: labels.existingPatients,
              value: chartData[groupId]?.existingPatients,
            },
          ],
          formatters: formatters.appendPercent,
          maxValues: {
            existingPatients: 100,
            newPatients: 100,
          },
          tooltipBottomContentRenderer: (data) => {
            return (
              <Text color='subdued' size='small' style={{ maxWidth: '230px' }}>
                <NSRTypeComparisonText
                  forTooltip
                  formerMetric={t('New patients')}
                  formerValue={data.newPatients}
                  latterMetric={t('Existing patients')}
                  latterValue={data.existingPatients}
                />
              </Text>
            );
          },
        }}
        data={chartData}
        onClick={({ group }) => {
          setFilters({
            locationIds: [group],
          });
        }}
      />
      <Chart.Legends noop />
      <ChartCTA
        buttonLabel={t('Send Reminder')}
        ctaType='sendReminders'
        title={t('Overall Highest No-Show')}
        highlight={highlight}
        description={description}
      />
    </Chart>
  );
};
